// garuda - Navigation Labyrinth Component

import React, { useState, useRef } from "react";
import { useNavigation, STAGES } from "../context/NavigationContext";
import { playPencilScratch, getTextBoundingBox } from "../utils/helpers";
import { HandDrawnCircle } from "./Icons";
import JackpotWord from "./JackpotWord";
import PageNavigation from "./PageNavigation";
import "./Labyrinth.css";

const Labyrinth = () => {
  const {
    currentStage,
    navigateTo,
    isTransitioning: isGlobalTransitioning,
  } = useNavigation();
  const [selectedOption, setSelectedOption] = useState(null);
  const [circleBbox, setCircleBbox] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const optionRefs = useRef({});

  const scenarios = {
    [STAGES.STAGE_1]: {
      text: "The room is quiet. A heavy silence sits on the floor.",
      options: [
        { id: "a", text: "Sit at the desk.", next: STAGES.STAGE_2A },
        { id: "b", text: "Walk toward the hallway.", next: STAGES.STAGE_2B },
      ],
    },
    [STAGES.STAGE_2A]: {
      text: "A single bulb flickers above a stack of papers.",
      options: [
        {
          id: "a",
          text: "Pick up the graphite.",
          message: "A sharp pencil lies waiting.",
          next: STAGES.PENCILS,
        },
        {
          id: "b",
          text: "Refill the fountain pen.",
          message: "The stars look like spilled ink.",
          next: STAGES.INKS,
        },
      ],
      hiddenTrap: {
        text: "A shadow shifts in the corner...",
        message: "The bulb flickers and dies. You are left in the dark.",
        trigger: "shadow",
      },
    },
    [STAGES.STAGE_2B]: {
      text: "The floorboards creak. Shadows stretch toward two doors.",
      options: [
        { id: "a", text: "Follow the faint ticking.", next: STAGES.STAGE_3A },
        { id: "b", text: "Follow the smell of dust.", next: STAGES.STAGE_3B },
      ],
    },
    [STAGES.STAGE_3A]: {
      text: "A clock on the wall tracks every stolen second.",
      options: [
        {
          id: "a",
          text: "Examine the gears.",
          message: "The pulse of the day is steady.",
          next: STAGES.ACTIVITIES,
        },
        {
          id: "b",
          text: "Clean your glasses.",
          message: "The world comes into focus.",
          next: STAGES.SPECTACLES,
        },
      ],
    },
    [STAGES.STAGE_3B]: {
      text: "A filing cabinet stands tall against the cold wall.",
      options: [
        {
          id: "a",
          text: "Search the 'Misc' folder.",
          message: "Tangled thoughts are filed away.",
          next: STAGES.NOTES,
        },
        {
          id: "b",
          text: "Check the floorboard for a key.",
          message: "The ledger is heavy and locked.",
          next: STAGES.VAULT,
        },
        {
          id: "c",
          text: "Open the window.",
          message: "The wind blows the candles out. Darkness is absolute.",
          trap: true,
        },
      ],
    },
  };

  const currentScenario = scenarios[currentStage];

  const handleOptionClick = (option, element) => {
    if (isTransitioning) return;

    // Get bounding box for circle animation
    const bbox = getTextBoundingBox(element);

    // Show circle first
    setCircleBbox(bbox);
    setSelectedOption(option.id);

    // Play sound when circle starts drawing
    playPencilScratch();
    setIsTransitioning(true);

    // Wait for circle animation to complete, then navigate
    setTimeout(() => {
      // Check if it's a trap (navigate to home with transition)
      if (option.trap) {
        navigateTo(STAGES.HOME);
        setTimeout(() => {
          setIsTransitioning(false);
          setCircleBbox(null);
          setSelectedOption(null);
        }, 100);
        return;
      }

      // Navigate immediately (transition overlay will take over)
      navigateTo(option.next);

      // Clean up after navigation starts
      setTimeout(() => {
        setIsTransitioning(false);
        setCircleBbox(null);
        setSelectedOption(null);
      }, 100);
    }, 800);
  };

  const handleHiddenTrap = () => {
    if (currentScenario.hiddenTrap) {
      playPencilScratch();
      setTimeout(() => {
        // Navigate to home with transition message
        navigateTo(STAGES.HOME);
      }, 1000);
    }
  };

  if (!currentScenario) return null;

  // Hide labyrinth during global transition
  if (isGlobalTransitioning) return null;

  return (
    <div className="labyrinth-container">
      <PageNavigation />
      <div
        className={`scenario-text ${isTransitioning ? "fade-out" : "fade-in"}`}
      >
        {currentScenario.text}
      </div>

      <div className="options-container">
        {currentScenario.options.map((option) => (
          <div
            key={option.id}
            className={`option ${selectedOption === option.id ? "selected" : ""}`}
            ref={(el) => (optionRefs.current[option.id] = el)}
            onClick={(e) => {
              const element = e.currentTarget;
              handleOptionClick(option, element);
            }}
            style={{ cursor: "pointer" }}
          >
            <span className="option-label">
              Option {option.id.toUpperCase()}:
            </span>
            <span className="option-text">
              <JackpotWord>{option.text}</JackpotWord>
            </span>
          </div>
        ))}
      </div>

      {/* Hidden trap for Stage 2A */}
      {currentStage === STAGES.STAGE_2A && currentScenario.hiddenTrap && (
        <div
          className="hidden-shadow"
          onMouseEnter={handleHiddenTrap}
          title="A shadow shifts..."
        />
      )}

      {/* Hand-drawn circle animation */}
      {circleBbox && (
        <HandDrawnCircle bbox={circleBbox} onAnimationComplete={() => {}} />
      )}
    </div>
  );
};

export default Labyrinth;
